import { useState, useEffect } from 'react';
import useMergedRef from '../useMergedRef/index.js';
const useForm = ({
  initialRef,
  initialErrors = {},
  initialMessages = {},
  defaultMessages = {}
} = {}) => {
  const formRef = useMergedRef(initialRef);
  const [errors, setErrors] = useState(initialErrors);
  const [messages, setMessages] = useState(initialMessages);
  const [submitted, setSubmitted] = useState(false);

  // Update if errors or messages are updated from the outside
  useEffect(() => {
    setErrors(initialErrors);
  }, [initialErrors]);
  useEffect(() => {
    setMessages(initialMessages);
  }, [initialMessages]);

  /**
   * Validate elements using HTML5 form validation.
   *
   * If a check fails we first check for any custom messages associated with the
   * input name, for the failed property. If there are no custom messages we
   * fall back to the browser defaults.
   *
   * @param {Array<HTMLElement>} elements - The elements to validate.
   * @param {boolean} [raiseError] - Flag to imperatively raise errors.
   */
  const getErrors = (elements, raiseError) => elements.reduce((newErrors, element) => {
    const {
      validity,
      name,
      validationMessage
    } = element;
    const customMessages = messages[name] || {};
    let failedProp;

    // Check ValidityState for any failed props. Note that as the props are
    // read-only and non-enumerable we cannot use Object.keys or Object.entries,
    // see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
    // eslint-disable-next-line no-restricted-syntax
    for (const key in validity) {
      if (validity[key] && key !== 'valid') {
        failedProp = key;
      }
    }

    // Get custom message or browser default
    const errorMsg = failedProp && (failedProp in customMessages || failedProp in defaultMessages) ? defaultMessages[failedProp] || customMessages[failedProp] : validationMessage;

    // If an error is imperatively raised, the form is being submitted,
    // or the check is for a custom error, set the error message,
    // otherwise clear errors for the input
    if ((raiseError || submitted || failedProp === 'customError') && errorMsg.length) {
      newErrors[name] = errorMsg;
    } else {
      delete newErrors[name];
    }
    return newErrors;
  }, {
    ...errors
  });

  /**
   * Validate a single input.
   *
   * @param {Event} evt - The event object.
   * @param {Function} [callback] - An optional callback function, will be called with the event.
   * @param {Object} [options] - Additional options.
   * @param {boolean} options.raiseError - Whether to update the errors in state,
   * thereby rendering or clearing errors in the UI. Enables real-time input validation.
   */
  const validateInput = (evt, callback, {
    raiseError = false
  } = {}) => {
    setErrors(getErrors([evt.target], raiseError));
    if (callback) {
      callback(evt);
    }
  };

  /**
   * Dispatch a custom submitted event.
   */
  const dispatchSubmittedEvent = detail => {
    const evt = new CustomEvent('submitted', {
      detail
    });
    formRef.current.dispatchEvent(evt);
  };

  /**
   * Handle form submission.
   */
  const handleSubmit = async (evt, callback) => {
    const {
      target
    } = evt;
    const {
      elements
    } = target;
    const invalidElements = [...elements].filter(el => !el.checkValidity());
    const newErrors = getErrors(invalidElements, true);
    const isValid = target.checkValidity();
    setSubmitted(true);
    setErrors(newErrors);

    // Scroll to the first invalid input
    if (invalidElements.length) {
      invalidElements[0].scrollIntoView({
        behavior: 'smooth'
      });
    }

    // Prevent submission if the form is invalid
    if (!isValid) {
      evt.preventDefault();
      dispatchSubmittedEvent(false);
      return;
    }
    if (!callback) {
      dispatchSubmittedEvent(true);
      return;
    }
    try {
      await callback(evt);
    } catch (err) {
      dispatchSubmittedEvent(false);
      throw err;
    }
    dispatchSubmittedEvent(true);
  };
  return {
    formRef,
    errors,
    validateInput,
    handleSubmit
  };
};
export default useForm;